// 从配置获取尺寸
const getSizeCfg = size => {
  const [width, height] = size.split(',').map(x => +x)
  return { width, height }
}

// 是否 IE 浏览器
const isIE = 'ActiveXObject' in window

export default {
  name: 'img-cropper',

  props: {
    // 图片源
    src: {
      type: String,
      required: true
    },

    // 弹框标题
    title: {
      type: String,
      default: '图片裁剪'
    },

    // 裁剪后的 base64 图片数据，需要 .sync 同步
    value: String,

    // 图片格式
    format: {
      type: String,
      default: 'png',
      validator: function(value) {
        return ['png', 'jpeg'].indexOf(value) !== -1
      }
    },

    // 是否可见，需要 .sync 同步
    visible: {
      type: Boolean,
      default: false
    },

    //扣章
    matting: {
      type: Boolean,
      default: false
    },

    // 是否可关闭 (关闭按钮是否显示)
    closeable: {
      type: Boolean,
      default: true
    },

    // 容器尺寸
    containerSize: {
      type: String,
      default: '300,300'
    },

    // 裁剪尺寸
    size: {
      type: String,
      default: '500,500'
    },

    // 裁剪区域是否为圆形
    round: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      // 图片原始尺寸
      natural: {
        width: 0,
        height: 0
      },

      // 代理操作图片
      proxy: {
        width: 0,
        height: 0,
        left: 0,
        top: 0,
        scale: 1 // 缩放
      }
    }
  },

  computed: {
    // 容器尺寸
    container() {
      return getSizeCfg(this.containerSize)
    },

    // 盒子宽度
    boxWidth() {
      return this.container.width + 14 * 2
    },

    // 裁剪尺寸
    crop() {
      return getSizeCfg(this.size)
    },

    // 裁剪区域
    area() {
      const { container } = this
      let { width: cw, height: ch } = this.crop
      let width, height

      if (cw > ch) {
        width = container.width * 0.8
        height = (width / cw) * ch
      } else {
        height = container.height * 0.8
        width = (height / ch) * cw
      }

      return { width, height }
    },

    // 间距
    space() {
      return {
        x: (this.container.width - this.area.width) / 2,
        y: (this.container.height - this.area.height) / 2
      }
    },

    // 原点
    o() {
      return {
        top: this.container.height / 2,
        left: this.container.width / 2
      }
    },

    // 定位范围
    range() {
      const {
        container: { width: cw, height: ch },
        proxy: { width: pw, height: ph },
        space: { x: spaceX, y: spaceY }
      } = this

      if (isIE) {
        const top = ch - spaceY - ph
        const left = cw - spaceX - pw

        return {
          maxTop: Math.max(top, spaceY),
          minTop: Math.min(top, spaceY),
          maxLeft: Math.max(left, spaceX),
          minLeft: Math.min(left, spaceX)
        }
      }

      return {
        maxTop: ch - spaceY,
        minTop: spaceY - ph,
        maxLeft: cw - spaceX,
        minLeft: spaceX - pw
      }
    },

    // 初始缩放
    initScale() {
      const { natural, area } = this
      const minX = area.width / natural.width
      const minY = area.height / natural.height

      return Math.max(minX, minY)
    },

    // 代理图片样式
    proxyStyle() {
      const { proxy } = this
      return `width: ${proxy.width}px; height: ${proxy.height}px; top: ${proxy.top}px; left: ${proxy.left}px`
    }
  },

  mounted() {
    document.body.appendChild(this.$el)
  },

  destroyed() {
    let $el = this.$el
    if ($el.parentElement) {
      $el.parentElement.removeChild($el)
    }
  },

  methods: {
    // 图片加载就绪
    imgLoaded(e) {
      const { naturalWidth: width, naturalHeight: height } = e.target

      this.natural = { width, height }

      this.reset()
    },

    // 重置图片位置尺寸
    reset() {
      const { container, natural, initScale: scale } = this
      const width = natural.width * scale
      const height = natural.height * scale

      // 代理图片尺寸定位
      Object.assign(this.proxy, {
        width,
        height,
        top: (container.height - height) / 2,
        left: (container.width - width) / 2,
        scale
      })
    },

    // 拖拽开始
    onDragStart(e) {
      const { pageX, pageY } = e
      const { left, top } = this.proxy

      // 记录初始信息
      this.dragData = {
        pageX,
        pageY,
        left,
        top,
        lastMove: +new Date()
      }

      document.addEventListener('mousemove', this.onDragMove, false)
      document.addEventListener('mouseup', this.onDragEnd, false)
    },

    // 拖拽中
    onDragMove(e) {
      const { pageX, pageY, left, top, lastMove } = this.dragData
      const now = +new Date()

      // 减少执行频率
      if (now - lastMove < 30) return

      this.dragData.lastMove = now

      Object.assign(
        this.proxy,
        this.adjustRange({
          left: left + e.pageX - pageX,
          top: top + e.pageY - pageY
        })
      )
    },

    // 拖拽结束
    onDragEnd() {
      this.dragData = null
      document.removeEventListener('mousemove', this.onDragMove, false)
      document.removeEventListener('mouseup', this.onDragEnd, false)
    },

    // 调整位置边界
    adjustRange({ left, top }) {
      const { maxTop, minTop, maxLeft, minLeft } = this.range

      if (top > maxTop) {
        top = maxTop
      } else if (top < minTop) {
        top = minTop
      }

      if (left > maxLeft) {
        left = maxLeft
      } else if (left < minLeft) {
        left = minLeft
      }

      return { left, top }
    },

    // 滚轮缩放
    onWheel(e) {
      const { proxy, natural, o, initScale } = this
      let scale = proxy.scale / initScale // 相对初始缩放的缩放比
      let _width = proxy.width
      let scaleChange = 0.05

      scale += (e.deltaY || -e.wheelDelta) < 0 ? scaleChange : -scaleChange

      if (isIE && scale < 1) scale = 1

      if (scale < 0.2) scale = 0.2

      const proxyScale = (proxy.scale = scale * initScale)

      proxy.width = natural.width * proxyScale
      proxy.height = natural.height * proxyScale

      let _scale = proxy.width / _width

      Object.assign(
        proxy,
        this.adjustRange({
          left: (proxy.left - o.left) * _scale + o.left,
          top: (proxy.top - o.top) * _scale + o.top
        })
      )
    },

    // 弹窗是否可见
    setVisible(isVisible = true) {
      this.$emit('update:visible', isVisible)
    },

    // 关闭弹窗
    close() {
      this.setVisible(false)
      this.$emit('cancel')
    },

    // 裁剪图片
    submit() {
      const { natural, proxy, area, crop, space, format } = this
      const { scale } = proxy

      const { canvas, img } = this.$refs
      const context = canvas.getContext('2d')

      let sx = -(proxy.left - space.x) / scale,
        sy = -(proxy.top - space.y) / scale,
        swidth = area.width / scale,
        sheight = area.height / scale,
        width = crop.width,
        height = crop.height,
        x = 0,
        y = 0

      // 清理 canvas
      context.clearRect(0, 0, width, height)

      // jpg 背景填白
      if (format === 'jpeg') {
        context.fillStyle = '#fff'
        context.fillRect(0, 0, width, height)
      }

      // IE 不支持负值
      if (isIE) {
        sx = Math.max(sx, 0)
        sy = Math.max(sy, 0)
      }

      // 绘制裁剪区域图像
      context.drawImage(img, sx, sy, swidth, sheight, x, y, width, height)

      if (this.matting) {
        let imageData = context.getImageData(0, 0, width, height)
        let data = imageData.data
        for (let i = 0; i < data.length; i += 4) {
          //对白色偏白色，进行透明处理
          let r = data[i],
            g = data[i + 1],
            b = data[i + 2]
          if ([r, g, b].every(v => v < 256 && b >= 185)) {
            //色值可调整
            data[i + 3] = 0
          }
        }
        context.putImageData(imageData, 0, 0)
      }

      // 获取图像数据
      const resultData = canvas.toDataURL('image/' + format)
      this.setVisible(false)
      this.$emit('update:value', resultData)
      this.$emit('success', resultData)
    }
  }
}
